/**
 * Keeping the slip's held price in step with the server's — pure helpers,
 * tested in quoteSync.test.ts.
 *
 * WHY THIS EXISTS. The server owns the price (migration 0036): POST /bets
 * carries the `quoteId` the client was shown and refuses with `quote_changed`
 * when the stored quote has moved. Racing quotes are refreshed from the
 * exchange every 15 seconds and team-sport quotes every catalogue sync, while
 * the board the customer is looking at is a snapshot. A customer who takes
 * twenty seconds to type a stake on a racing price is therefore holding a
 * token the server has already replaced — which is exactly the "The price has
 * changed, every time" report from testing. The refusal is correct; the client
 * handling it was not. Three things fix it:
 *
 *   1. While a selection is held, re-resolve it against every fresh /markets
 *      payload (`resolveHeldSelection`) so the slip shows the live price and
 *      submits the live token. Most movement is absorbed here, silently, with
 *      a small "price updated" note.
 *   2. When the server still refuses with `quote_changed`, it returns the
 *      CURRENT terms in `details` (same values /markets serves). Apply them
 *      (`quoteFromChangedDetails`) and let the customer accept with one tap,
 *      instead of sending them back to the board.
 *   3. Stale/expired refusals carry no replacement terms; the remedy is a
 *      board refetch and re-resolution, same path as (1).
 *
 * Nothing here decides a price. These helpers only move server-issued terms
 * from one place to another.
 */
import type { Market, Selection } from '@/types/api';

/** Error codes POST /bets returns when the held quote is no longer the live one. */
export const QUOTE_REFUSAL_CODES = new Set([
  'quote_changed',
  'quote_expired',
  'quote_stale',
  'delayed_price_stale',
  'quote_missing',
]);

/** Refusals that mean the board itself is unplaceable right now, not this quote. */
export const BOARD_REFUSAL_CODES = new Set(['market_catalogue_stale', 'market_posture_stale']);

export interface HeldSelection {
  market: Market;
  selection: Selection;
}

/**
 * Find the held selection in a fresh board. Matches on market id AND
 * selection id — both are durable (0031) across polls — and returns the fresh
 * objects, or null when the market or runner has left the board.
 */
export function resolveHeldSelection(markets: Market[] | undefined, held: HeldSelection | null): HeldSelection | null {
  if (!markets || !held) return null;
  const market = markets.find((m) => m.id === held.market.id);
  if (!market) return null;
  const selection = market.selections.find((s) => s.id === held.selection.id);
  if (!selection) return null;
  return { market, selection };
}

/** True when the fresh selection carries a different server token than the held one. */
export function quoteMoved(held: Selection, fresh: Selection): boolean {
  return held.quoteId !== fresh.quoteId || held.decimalOdds !== fresh.decimalOdds;
}

/**
 * Pull the replacement terms out of a `quote_changed` error's `details`.
 * The server returns `{ quoteId, decimalOdds, expiresAt }`; `decimalOdds`
 * arrives as the NUMERIC's canonical string ('2.4500'), so it is coerced.
 * Returns null when the payload is not the shape we expect — the caller then
 * falls back to a board refetch rather than trusting a partial object.
 */
export function quoteFromChangedDetails(
  held: Selection,
  details: unknown,
): Selection | null {
  if (!details || typeof details !== 'object') return null;
  const d = details as Record<string, unknown>;
  const quoteId = typeof d.quoteId === 'string' ? d.quoteId : null;
  const odds = typeof d.decimalOdds === 'number' ? d.decimalOdds : typeof d.decimalOdds === 'string' ? Number(d.decimalOdds) : NaN;
  const expiresAt = typeof d.expiresAt === 'string' ? d.expiresAt : held.expiresAt;
  if (!quoteId || !Number.isFinite(odds) || odds <= 1) return null;
  return { ...held, quoteId, decimalOdds: odds, expiresAt };
}

export interface PriceChange {
  fromOdds: number;
  toOdds: number;
}

/** Plain words for the slip's price-change notice. */
export function describePriceChange(c: PriceChange): string {
  const dir = c.toOdds > c.fromOdds ? 'up' : 'down';
  return `Price moved ${dir}: ${c.fromOdds.toFixed(2)} → ${c.toOdds.toFixed(2)}`;
}
